/* eslint-disable react/no-danger */

import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Helmet } from 'react-helmet';
import { t } from 'ttag';

import { Heading } from '@components';
import { Breadcrumbs } from '@modules';

import { Contact, Questions } from '../components';

import './Question.css';

const fetcher = url => fetch(url).then(r => r.json());

export default function Question() {
  const history = useHistory();
  const { question } = useParams();

  const { data: questionData, isLoading: isQuestionLoading } = useSWR(
    `/api/faq/questions/${question}/`,
    fetcher
  );

  const formatDate = useCallback(dateString => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }, []);

  const handleHistoryPush = useCallback(
    (event, url) => {
      event.preventDefault();
      history.push(url);
    },
    [history]
  );

  return (
    <>
      {questionData?.data?.attributes?.seo_title && (
        <Helmet>
          <title>{questionData?.data?.attributes?.seo_title}</title>
        </Helmet>
      )}
      {questionData?.data?.attributes?.seo_description && (
        <Helmet>
          <meta
            name="description"
            content={questionData?.data?.attributes?.seo_description}
          />
        </Helmet>
      )}

      <div className="min-h-screen bg-whisper pb-8 md:pb-16">
        <div className="container px-0 py-4 sm:px-2 sm:py-8 md:space-y-16">
          <section>
            <Breadcrumbs>
              <Breadcrumbs.Breadcrumb href="/">{t`Frontpage`}</Breadcrumbs.Breadcrumb>
              <Breadcrumbs.Breadcrumb
                href="/faq/"
                onClick={event => handleHistoryPush(event, '/')}
              >
                {t`FAQ`}
              </Breadcrumbs.Breadcrumb>
              <Breadcrumbs.Breadcrumb
                href={`/faq/${questionData?.data?.attributes?.category?.url_name}/`}
                onClick={event =>
                  handleHistoryPush(
                    event,
                    `/${questionData?.data?.attributes?.category?.url_name}/`
                  )
                }
              >
                {questionData?.data?.attributes?.category?.title || ''}
              </Breadcrumbs.Breadcrumb>
              <Breadcrumbs.Breadcrumb
                href={`/faq/${questionData?.data?.attributes?.category?.url_name}/${questionData?.data?.attributes?.url_name}/`}
                isActive
                onClick={event =>
                  handleHistoryPush(
                    event,
                    `/${questionData?.data?.attributes?.category?.url_name}/${questionData?.data?.attributes?.url_name}/`
                  )
                }
              >
                {questionData?.data?.attributes?.text}
              </Breadcrumbs.Breadcrumb>
            </Breadcrumbs>
          </section>
          <main className="space-y-8 lg:grid lg:grid-cols-3 lg:gap-16 lg:space-y-0">
            <section className="space-y-8 lg:order-2 lg:col-span-2">
              {isQuestionLoading ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-12 w-12 animate-spin fill-black"
                >
                  <path d="M12 3C16.9706 3 21 7.02944 21 12H19C19 8.13401 15.866 5 12 5V3Z" />
                </svg>
              ) : (
                <>
                  <header>
                    <a
                      href={`/faq/${questionData?.data?.attributes?.category?.url_name}/`}
                      onClick={event =>
                        handleHistoryPush(
                          event,
                          `/${questionData?.data?.attributes?.category?.url_name}/`
                        )
                      }
                      className="m-0 flex items-center gap-4 border-0 bg-transparent fill-current py-4 text-black no-underline focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                      >
                        <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z" />
                      </svg>
                      <span className="leading-none">{t`Back`}</span>
                    </a>
                    <Heading className="break-words">
                      {questionData?.data?.attributes?.text || ''}
                    </Heading>
                    <span className="text-dark-gray">
                      {t`Last Updated`}
                      {': '}
                      {formatDate(
                        questionData?.data?.attributes?.updated_at ||
                          '2023-01-01T12:00:00.000000'
                      )}
                    </span>
                  </header>
                  <article
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: questionData?.data?.attributes?.answer_text
                    }}
                  />
                  <Contact />
                </>
              )}
            </section>
            <aside className="space-y-4 lg:order-1 lg:col-span-1">
              <Heading as="h2" className="break-words font-black">
                {questionData?.data?.attributes?.category?.title || ''}
              </Heading>
              <Questions />
            </aside>
          </main>
        </div>
      </div>
    </>
  );
}
